// @ts-nocheck
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowsRightLeftIcon,
  Bars3Icon,
  BellIcon,
  HomeIcon,
  Square3Stack3DIcon,
  UserCircleIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import BrandLogo from "../../components/brand/BrandLogo";
import LanguageSelector from "../../components/language/LanguageSelector";
import { useTranslation } from "../../i18n/LanguageContext";
import LogoutModal from "../../scenes/login/Logout";
import { URL } from "../../service/Service";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function OficialSidebar({
                                         sidebarOpens,
                                         setSidebarOpens,
                                         opens,
                                         onToggles,
                                       }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [unreadCount, setUnreadCount] = useState(0);

  const navigation = useMemo(
      () => [
        {
          name: t('nav.dashboard'),
          href: "/user/dashboard",
          icon: HomeIcon,
        },
        {
          name: t('nav.profile'),
          href: "/user/geral",
          icon: UserCircleIcon,
        },
        {
          name: t('nav.clients'),
          href: "/user/clientes",
          icon: UsersIcon,
        },
        {
          name: t('nav.rates'),
          href: "/user/taxas",
          icon: Square3Stack3DIcon,
        },
        {
          name: t('nav.transactions'),
          href: "/user/levantamento",
          icon: ArrowsRightLeftIcon,
        },
      ],
      [t]
  );

  const fetchUnreadNotifications = useCallback(async () => {
    try {
      const jwtToken = sessionStorage.getItem("jwt");

      if (!jwtToken) {
        setUnreadCount(0);
        return;
      }

      const response = await axios.get(`${URL}/api/user/unread`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      setUnreadCount(Number(response.data) || 0);
    } catch (error) {
      console.error("Error fetching unread notifications count:", error);
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    fetchUnreadNotifications();
  }, [fetchUnreadNotifications, location.pathname]);

  useEffect(() => {
    const handleRefreshNotifications = () => {
      fetchUnreadNotifications();
    };

    const handleWindowFocus = () => {
      fetchUnreadNotifications();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUnreadNotifications();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("notifications:refresh", handleRefreshNotifications);
    window.addEventListener("notifications:changed", handleRefreshNotifications);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const intervalId = window.setInterval(() => {
      fetchUnreadNotifications();
    }, 30000);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("notifications:refresh", handleRefreshNotifications);
      window.removeEventListener("notifications:changed", handleRefreshNotifications);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [fetchUnreadNotifications]);

  function goTo(path: string) {
    navigate(path);

    if (setSidebarOpens) {
      setSidebarOpens(false);
    }

    if (path === "/user/notifications") {
      window.setTimeout(() => {
        fetchUnreadNotifications();
      }, 500);

      window.setTimeout(() => {
        fetchUnreadNotifications();
      }, 1500);
    }
  }

  function isActive(path: string) {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  }

  function SidebarNavItem({ item, collapsed = false }) {
    const active = isActive(item.href);

    return (
        <button
            type="button"
            onClick={() => goTo(item.href)}
            title={collapsed ? item.name : undefined}
            className={classNames(
                "sidebar-nav-item group relative flex w-full items-center text-sm font-semibold",
                collapsed
                    ? "justify-center px-0 py-3"
                    : "justify-start gap-3 px-4 py-3",
                active && "sidebar-nav-item--active"
            )}
        >
          <span className="sidebar-nav-icon">
            <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          </span>

          {!collapsed && <span className="sidebar-nav-label truncate">{item.name}</span>}

          {collapsed && (
              <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block">
            {item.name}
          </span>
          )}
        </button>
    );
  }

  function NotificationButton({ collapsed = false }) {
    const { t } = useTranslation();
    const active = isActive("/user/notifications");

    return (
        <button
            type="button"
            onClick={() => goTo("/user/notifications")}
            title={t('nav.notifications')}
            className={classNames(
                "sidebar-nav-item group relative flex w-full items-center text-sm font-semibold",
                collapsed
                    ? "justify-center px-0 py-3"
                    : "justify-start gap-3 px-4 py-3",
                active && "sidebar-nav-item--active"
            )}
        >
        <span className="sidebar-nav-icon relative flex">
          <BellIcon
              className={classNames(
                  "h-6 w-6 flex-shrink-0 transition-colors",
                  active ? "text-[#18d4bd]" : "text-white/60 group-hover:text-[#18d4bd]"
              )}
              aria-hidden="true"
          />

          {unreadCount > 0 && (
              <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-leve px-1 text-[10px] font-bold text-white shadow">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </span>

          {!collapsed && (
              <div className="flex min-w-0 flex-col items-start">
                <span className="truncate">{t('nav.notifications')}</span>

                {unreadCount > 0 && (
                    <span className="text-xs font-normal text-gray-300">
                {unreadCount === 1 ? "1 por ler" : `${unreadCount} por ler`}
              </span>
                )}
              </div>
          )}

          {collapsed && (
              <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block">
            {unreadCount > 0
                ? `${unreadCount} ${
                    unreadCount === 1 ? "notificação" : "notificações"
                } por ler`
                : t('nav.notifications')}
          </span>
          )}
        </button>
    );
  }

  const sidebarBackground = "brand-sidebar text-white/70";

  return (
      <>
        <div>
          <Transition.Root show={sidebarOpens} as={Fragment}>
            <Dialog
                as="div"
                className="fixed inset-0 z-50 flex md:hidden"
                onClose={setSidebarOpens}
            >
              <Transition.Child
                  as={Fragment}
                  enter="transition-opacity ease-linear duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity ease-linear duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-black/60" />
              </Transition.Child>

              <Transition.Child
                  as={Fragment}
                  enter="transition ease-in-out duration-300 transform"
                  enterFrom="-translate-x-full"
                  enterTo="translate-x-0"
                  leave="transition ease-in-out duration-300 transform"
                  leaveFrom="translate-x-0"
                  leaveTo="-translate-x-full"
              >
                <div className="brand-sidebar sidebar-shell relative flex h-full w-full max-w-xs flex-col shadow-2xl">
                  <div className="flex items-center justify-between px-5 py-5">
                    <BrandLogo />

                    <button
                        type="button"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20 focus:outline-none"
                        onClick={() => setSidebarOpens(false)}
                        title="Fechar menu"
                    >
                      <span className="sr-only">Fechar menu</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <nav className="sidebar-nav-list flex-1 space-y-2 px-4 py-4">
                    {navigation.map((item) => (
                        <SidebarNavItem
                            key={item.name}
                            item={item}
                            collapsed={false}
                        />
                    ))}
                  </nav>

                  <div className="flex justify-center px-4 pb-4">
                    <LanguageSelector dark placement="top" />
                  </div>

                  <div className="space-y-2 border-t border-white/10 px-4 py-4">
                    <NotificationButton collapsed={false} />

                    <div className="rounded-xl transition hover:bg-white/10">
                      <LogoutModal isOpen={true} />
                    </div>
                  </div>
                </div>
              </Transition.Child>
            </Dialog>
          </Transition.Root>

          <div className="hidden md:fixed md:inset-y-0 md:left-0 md:z-40 md:flex md:flex-col">
            <aside
                className={classNames(
                    "sidebar-shell relative flex h-screen flex-col border-r border-white/10 shadow-xl transition-all duration-300",
                    sidebarBackground,
                    opens ? "w-64" : "w-20"
                )}
            >
              <div className="relative flex items-center px-4 pt-8 pb-6">
                {opens ? (
                    <BrandLogo />
                ) : (
                    <BrandLogo compact className="mx-auto" />
                )}

                <button
                    type="button"
                    onClick={onToggles}
                    className="absolute -right-4 top-24 z-50 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-blue-green-dark text-white shadow-lg transition hover:scale-105 hover:bg-green-cl focus:outline-none"
                    title={opens ? t('nav.close') : t('nav.open')}
                >
                <span className="sr-only">
                  {opens ? t('nav.close') : t('nav.open')}
                </span>

                  {opens ? (
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                      <Bars3Icon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>

              <nav className="sidebar-nav-list flex-1 space-y-2 px-3">
                {navigation.map((item) => (
                    <SidebarNavItem
                        key={item.name}
                        item={item}
                        collapsed={!opens}
                    />
                ))}
              </nav>

              <div className="flex justify-center px-3 pb-4">
                <LanguageSelector dark compact={!opens} placement="top" />
              </div>

              <div className="space-y-2 border-t border-white/10 px-3 py-4">
                <NotificationButton collapsed={!opens} />

                <div
                    className={classNames(
                        "rounded-xl transition hover:bg-white/10",
                        opens ? "px-1" : "flex justify-center"
                    )}
                >
                  <LogoutModal isOpen={opens} />
                </div>
              </div>
            </aside>
          </div>

          <div className="flex flex-1 flex-col md:hidden">
            <div className="sticky top-0 z-30 px-3 py-3">
              {!sidebarOpens && (
                  <button
                      type="button"
                      className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-green-dark text-white shadow-lg transition hover:bg-green-cl focus:outline-none"
                      onClick={() => setSidebarOpens(true)}
                      title={t('nav.open')}
                  >
                    <span className="sr-only">Abrir menu</span>
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  </button>
              )}
            </div>
          </div>
        </div>
      </>
  );
}
